import type { AnalyticsProvider, EventProperties, UserProperties } from './types';

interface QueuedEvent {
  type: 'identify' | 'track' | 'deidentify';
  args: [string, (UserProperties | EventProperties)?];
}

/**
 * Analytics service that manages multiple analytics providers
 * and dispatches events to all active providers.
 *
 * Events called before any provider is registered are queued
 * and flushed when the first provider is added.
 */
export class AnalyticsService {
  private providers: AnalyticsProvider[] = [];
  private eventQueue: QueuedEvent[] = [];
  private ready = false;

  /**
   * Register an analytics provider.
   * When the first provider is added, any queued events are flushed.
   */
  addProvider(provider: AnalyticsProvider): void {
    this.providers.push(provider);
    if (!this.ready) {
      this.ready = true;
    }
    this.flushQueueToProvider(provider);
  }

  /**
   * Replay queued events to a newly added provider.
   */
  private flushQueueToProvider(provider: AnalyticsProvider): void {
    this.eventQueue.forEach(({ type, args }) => {
      try {
        if (type === 'identify') {
          provider.identify(args[0], args[1] as UserProperties | undefined);
        } else if (type === 'deidentify') {
          provider.deidentify?.();
        } else {
          provider.track(args[0], args[1] as EventProperties | undefined);
        }
      } catch (error) {
        console.error(`Failed to flush queued event to ${provider.name}:`, error);
      }
    });
  }

  /**
   * Identify a user across all active providers
   */
  identify(userId: string, properties?: UserProperties): void {
    if (!this.ready) {
      this.eventQueue.push({ type: 'identify', args: [userId, properties] });
      return;
    }
    this.dispatchIdentify(userId, properties);
  }

  /**
   * Dispatch identify to all providers
   */
  private dispatchIdentify(userId: string, properties?: UserProperties): void {
    this.providers.forEach((provider) => {
      try {
        provider.identify(userId, properties);
      } catch (error) {
        console.error(`Failed to identify user in ${provider.name}:`, error);
      }
    });
  }

  /**
   * Track an event across all active providers
   */
  track(eventName: string, properties?: EventProperties): void {
    if (!this.ready) {
      this.eventQueue.push({ type: 'track', args: [eventName, properties] });
      return;
    }
    this.dispatchTrack(eventName, properties);
  }

  /**
   * Dispatch track to all providers
   */
  private dispatchTrack(eventName: string, properties?: EventProperties): void {
    this.providers.forEach((provider) => {
      try {
        provider.track(eventName, properties);
      } catch (error) {
        console.error(`Failed to track event in ${provider.name}:`, error);
      }
    });
  }

  /**
   * De-identify the current user across all active providers
   */
  deidentify(): void {
    if (!this.ready) {
      this.eventQueue.push({ type: 'deidentify', args: [''] });
      return;
    }
    this.dispatchDeidentify();
  }

  /**
   * Dispatch deidentify to all providers
   */
  private dispatchDeidentify(): void {
    this.providers.forEach((provider) => {
      try {
        provider.deidentify?.();
      } catch (error) {
        console.error(`Failed to de-identify user in ${provider.name}:`, error);
      }
    });
  }

  /**
   * Get all registered providers
   */
  getProviders(): AnalyticsProvider[] {
    return [...this.providers];
  }

  /**
   * Get active providers
   */
  getActiveProviders(): AnalyticsProvider[] {
    return this.providers.filter((p) => p.isActive());
  }

  /**
   * Check if any of the analytics providers are Active
   */
  hasActiveProviders(): boolean {
    return this.providers.some((p) => p.isActive());
  }
}

// Singleton instance
export const analytics = new AnalyticsService();
